import {
  automationManagerScopeSchema,
  type AutomationManagerRecipients,
} from "@repo/app-types/entities/automation";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import type { FC } from "react";
import { TeammatePicker, type TeammateOption } from "./teammatePicker.js";

export interface ManagerScopeFieldProps {
  value: AutomationManagerRecipients;
  teammateOptions: readonly TeammateOption[];
  /** Disables the "specific managers" picker while its options load. */
  isLoading?: boolean;
  /** Renders the whole field disabled, e.g. for read-only viewers. */
  isDisabled?: boolean;
  onChange: (value: AutomationManagerRecipients) => void;
}

/**
 * Radio choice between all managers and a hand-picked set, revealing a
 * {@link TeammatePicker} for the "specific managers" case.
 */
export const ManagerScopeField: FC<ManagerScopeFieldProps> = (props) => {
  const { value, teammateOptions, isLoading, isDisabled, onChange } = props;

  return (
    <div className="flex flex-col gap-2">
      <SimpleRadioGroup
        value={value.scope}
        onChange={(nextValue) => {
          const scope = automationManagerScopeSchema.parse(nextValue);
          onChange(
            scope === "specific_managers"
              ? { scope, teammate_ids: [] }
              : { scope },
          );
        }}
      >
        <SimpleRadioOption value="all_managers" isDisabled={isDisabled}>
          All managers
        </SimpleRadioOption>
        <SimpleRadioOption value="specific_managers" isDisabled={isDisabled}>
          Specific managers
        </SimpleRadioOption>
      </SimpleRadioGroup>
      {value.scope === "specific_managers" ? (
        <TeammatePicker
          placeholder="Select managers"
          ariaLabel="Specific managers to notify"
          teammateOptions={teammateOptions}
          isLoading={isLoading}
          isDisabled={isDisabled}
          value={value.teammate_ids}
          onChange={(nextTeammateIds) => {
            onChange({
              scope: "specific_managers",
              teammate_ids: [...nextTeammateIds],
            });
          }}
        />
      ) : null}
    </div>
  );
};
